import React, { useEffect, useState, useMemo, useRef } from "react";
import { HiPaperClip } from "react-icons/hi";
import { FaUserCircle, FaPrint } from "react-icons/fa";
import { FiX, FiDownload, FiChevronLeft, FiChevronRight, FiCopy, FiCheck, FiZoomIn, FiZoomOut } from "react-icons/fi";
import TicketForm from "./TicketForm";
import api from "../utils/api";
import { hasRole } from "../utils/roleUtils";
import Loader from "../components/Loader";
import { getStatusLabel } from "../utils/statusStyles";

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy'}
      style={{ lineHeight: 0, verticalAlign: 'middle', marginLeft: '6px', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
      className="text-gray-300 hover:text-gray-600 transition-colors cursor-pointer"
    >
      {copied
        ? <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 700 }}>✓ Copied</span>
        : <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
      }
    </button>
  );
}

const makeLinksSafeAndUserFriendly = (html) => {
  if (!html) return "";
  return html.replace(/<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/g, (match, quote, url) => {
    return `<a href=${quote}${url}${quote} target="_blank" rel="noopener noreferrer"`;
  });
};

export default function TicketDetails({
  ticketId,
  tickets = [],
  onClose,
  priorityOptions = [],
  statusOptions = [],
  userInfo = {},
  selectedClientId = "",
  selectedTicketChildId = "",
  onSuccess,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
}) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });
  const [currentId, setCurrentId] = useState(ticketId);
  const imageContainerRef = useRef(null);

  // Reset zoom & pan position when switching images
  useEffect(() => {
    setZoomScale(1);
    setPanPosition({ x: 0, y: 0 });
  }, [activeImageIndex]);

  // Wheel zoom handler
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.15 : -0.15;
      setZoomScale((prev) => {
        const nextZoom = Number((prev + delta).toFixed(2));
        const clamped = Math.min(4.0, Math.max(0.5, nextZoom));
        if (clamped === 1) setPanPosition({ x: 0, y: 0 });
        return clamped;
      });
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [activeImageIndex, previewImages]);

  const handleZoomIn = () => setZoomScale((prev) => Math.min(4.0, Number((prev + 0.25).toFixed(2))));
  const handleZoomOut = () => setZoomScale((prev) => {
    const next = Math.max(0.5, Number((prev - 0.25).toFixed(2)));
    if (next === 1) setPanPosition({ x: 0, y: 0 });
    return next;
  });
  const handleResetZoom = () => {
    setZoomScale(1);
    setPanPosition({ x: 0, y: 0 });
  };

  // Click & Drag Pan Handlers
  const handleMouseDown = (e) => {
    if (zoomScale <= 1) return;
    e.preventDefault();
    setIsPanning(true);
    setStartPan({
      x: e.clientX - panPosition.x,
      y: e.clientY - panPosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isPanning || zoomScale <= 1) return;
    e.preventDefault();
    setPanPosition({
      x: e.clientX - startPan.x,
      y: e.clientY - startPan.y
    });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  useEffect(() => {
    setCurrentId(ticketId);
  }, [ticketId]);

  const currentIndex = useMemo(() => {
    if (!Array.isArray(tickets) || tickets.length === 0) return -1;
    return tickets.findIndex(
      (t) => String(t.id || t.ticket_id) === String(currentId)
    );
  }, [tickets, currentId]);

  const effectiveOnNext = onNext || (tickets.length > 0 && currentIndex >= 0 && currentIndex < tickets.length - 1
    ? () => {
        const nextTicket = tickets[currentIndex + 1];
        if (nextTicket) setCurrentId(nextTicket.id || nextTicket.ticket_id);
      }
    : undefined);

  const effectiveOnPrevious = onPrevious || (tickets.length > 0 && currentIndex > 0
    ? () => {
        const prevTicket = tickets[currentIndex - 1];
        if (prevTicket) setCurrentId(prevTicket.id || prevTicket.ticket_id);
      }
    : undefined);

  const effectiveHasNext = hasNext !== undefined ? hasNext : (tickets.length > 0 && currentIndex >= 0 && currentIndex < tickets.length - 1);
  const effectiveHasPrevious = hasPrevious !== undefined ? hasPrevious : (tickets.length > 0 && currentIndex > 0);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = String(date.getFullYear());
    const h = String(date.getHours()).padStart(2, "0");
    const i = String(date.getMinutes()).padStart(2, "0");
    const s = String(date.getSeconds()).padStart(2, "0");
    return `${d}-${m}-${y} ${h}:${i}:${s}`;
  };

  const IMAGE_BASE_URL = process.env.REACT_APP_IMAGEURL;

  useEffect(() => {
    const fetchTicketDetails = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/ticket/${currentId}`);
        if (res.data?.status) {
          setTicket(res.data.data);
        }
      } catch (err) {
        console.error("Failed to load ticket details", err);
      } finally {
        setLoading(false);
      }
    };
    if (currentId) fetchTicketDetails();
  }, [currentId]);

  const toCamelCase = (str) =>
    str
      ?.toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const handlePrint = () => {
    const originalContents = document.body.innerHTML;
    const printContents = document.getElementById("printableDiv").innerHTML;
    const printHeader = document.getElementById("printableDivHeader").innerHTML;

    // Concatenate header + content
    document.body.innerHTML = printHeader + printContents;
    window.print();
    // Restore original page
    document.body.innerHTML = originalContents;

    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 text-main">
        <Loader />
      </div>
    );
  }
  if (!ticket) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-40"
    >
      <div
        className="relative top-0 right-0 w-full md:w-[900px] h-full bg-white shadow-2xl border-l z-50 flex flex-col"
        onClick={(e) => e.stopPropagation()} // stop close on inside click
      >
        {/* Header */}
        <div
          id="printableDivHeader"
          className="flex items-center justify-between px-3 py-4 sticky top-0 z-10 bg-white"
        >
          <div className="flex-1 space-y-1 overflow-hidden pr-20 sm:pr-24">
            <h2 className="text-sm xs:text-xs sm:text-base md:text-lg lg:text-xl font-medium leading-5">
              #{ticket.ticket_master_id} - {ticket.title}
            </h2>

            <div className="flex flex-wrap gap-3 text-sm font-medium">
              <span className="border border-white/40 px-2 py-0.5 rounded">
                Client: {ticket.client_name}
              </span>
              <span className="border border-white/40 px-2 py-0.5 rounded">
                Module: {ticket.module_name}
              </span>
              <span className="border border-white/40 px-2 py-0.5 rounded">
                Status: {(Number(userInfo?.role_id) === 2 && [3, 12].includes(Number(ticket.status_id)) && String(ticket.assign || ticket.assign_user_id) !== String(userInfo?.user_id)) ? "IN process" : ([2, 17].includes(Number(userInfo?.role_id)) && [7, 8, 9, 10, 11, 13, 14, 15, 16, 17, 18, 19, 20].includes(Number(ticket.status_id))) ? "IN process" : (Number(ticket.status_id) === 14 && !hasRole(userInfo, 8) && String(ticket.assign || ticket.assign_user_id) !== String(userInfo?.user_id)) ? "Waiting for Code Review" : (Number(ticket.status_id) === 17 && !hasRole(userInfo, 6) && String(ticket.assign || ticket.assign_user_id) !== String(userInfo?.user_id)) ? "Waiting for Production Deployment" : getStatusLabel(ticket.status_id, ticket.status)}
              </span>
              {([14, 15, 16, 17, 18, 19].includes(Number(ticket.status_id)) || ticket.assigned_to) && (
                <span className="border border-white/40 px-2 py-0.5 rounded">
                  {[2, 17].includes(Number(userInfo?.role_id)) ? "Assigned To: " : Number(ticket.status_id) === 14 ? "Code Reviewer: " : Number(ticket.status_id) === 17 ? "Deployer: " : "Assigned To: "}{ticket.assigned_to || "-"}
                </span>
              )}
              <span className="border border-white/40 px-2 py-0.5 rounded">
                Reported By:{" "}
                {toCamelCase(
                  (ticket.customer_first_name || ticket.customer_last_name)
                    ? `${ticket.customer_first_name || ''} ${ticket.customer_last_name || ''}`.trim()
                    : (ticket.comments?.slice(-1)[0]?.by || "-")
                )}
              </span>
              {ticket.internal_cc_names && !([2, 17].includes(Number(userInfo?.role_id ?? userInfo?.user_role_id))) && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm font-medium text-gray-700">Internal CC:</span>
                  <div className="flex flex-wrap gap-1">
                    {ticket.internal_cc_names.split(',').map((name, idx) => (
                      <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {name.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Buttons container */}
          <div className="absolute top-1 right-2 flex space-x-2 items-center">
            {/* Navigation Controls Removed from top */}

            {/* Print Button */}
            <button
              onClick={handlePrint}
              className="w-8 h-8 bg-black-600 text-black rounded-full flex items-center justify-center hover:text-white hover:bg-red-700 transition"
              aria-label="Print"
            >
              <FaPrint className="w-4 h-4" />
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-200 hover:text-black transition"
              aria-label="Close ticket details"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Update Form */}
          <div className="px-3 pb-4">
            <TicketForm
              isAdmin={false}
              onSuccess={onSuccess}
              onClose={onClose}
              priorityOptions={priorityOptions}
              statusOptions={statusOptions}
              userInfo={userInfo}
              selectedClientId={selectedClientId}
              selectedTicketId={currentId}
              selectedStatusId={ticket.status_id}
              selectedPriorityId={ticket.priority_id}
              selectedTicketChildId={selectedTicketChildId || ticket?.customer_name || ticket?.child_id || ''}
              module_name={ticket?.module_name}
              selectedIssueType={ticket?.issue_type}
              ticket={ticket}
              onNext={effectiveOnNext}
              onPrevious={effectiveOnPrevious}
              hasNext={effectiveHasNext}
              hasPrevious={effectiveHasPrevious}
            />
          </div>

          {/* Comments */}
          <div id="printableDiv" className="px-3 py-3 space-y-3">
            {(ticket.comments || [])
              .filter((c) => {
                if ([2, 17].includes(Number(userInfo?.role_id))) {
                  // Show comments from Roles 1 (Admin/VH), 2 (Customer), 3 (Client Advocate), and 17 (Client Admin)
                  // AND hide internal process comments (Statuses 7-11, 13, 14-20)
                  const isInternalStatus = [7, 8, 9, 10, 11, 13, 14, 15, 16, 17, 18, 19, 20].includes(Number(c.status_id));
                  return ([1, 2, 3, 17].includes(Number(c.user_role)) || Number(c.updated_by) === 101) && !isInternalStatus;
                }
                return true;
              })
              .map((c) => (
                <div
                  key={c.comment_id}
                  className="rounded-xl border border-gray-200 bg-white shadow-md p-4 relative overflow-hidden hover:shadow-lg transition-all"
                >
                  <div className="flex justify-between flex-wrap sm:flex-nowrap sm:justify-between sm:items-start">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full overflow-hidden">
                        <img
                          src={
                            c?.user_img
                              ? `${IMAGE_BASE_URL}/${c.user_img}`
                              : `${IMAGE_BASE_URL}/uploads/no_image.png` // fallback image
                          }
                          alt="Profile"
                          className="h-full w-full object-cover rounded-full"
                        />
                      </span>

                      <div>
                        <p className="text-sm font-semibold text-main">
                          {toCamelCase(c.by)}
                        </p>
                        <p className="text-xs text-main">{formatDate(c.on)}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 text-sm mt-2 sm:mt-0">
                      <span
                        className={`px-2 py-1 rounded border text-xs font-semibold ${c.status === "Reopen"
                            ? "border-red-500 text-red-600"
                            : c.status === "Closed"
                              ? "border-gray-500 text-gray-600"
                              : c.status === "Pending"
                                ? "border-yellow-500 text-yellow-600"
                                : "border-blue-500 text-blue-600"
                          } bg-white`}
                      >
                        Status: {(Number(userInfo?.role_id) === 2 && [3, 12].includes(Number(c.status_id)) && String(ticket.assign || ticket.assign_user_id) !== String(userInfo?.user_id)) ? "IN process" : [2, 17].includes(Number(userInfo?.role_id)) && [7, 8, 9, 10, 11, 13, 14, 15, 16, 17, 18, 19, 20].includes(Number(c.status_id)) ? "IN process" : c.status == "Reopen" ? "Reopened" : c.status}
                      </span>
                      <span
                        className={`px-2 py-1 rounded border text-xs font-semibold bg-white ${c.priority === "Critical"
                            ? "border-red-700 text-red-700"
                            : c.priority === "High"
                              ? "border-orange-500 text-orange-500"
                              : c.priority === "Medium"
                                ? "border-yellow-400 text-yellow-500"
                                : "border-green-500 text-green-500"
                          }`}
                      >
                        Priority: {c.priority}
                      </span>
                      <span className="px-2 py-1 rounded border border-emerald-600 text-emerald-600 bg-white text-xs font-semibold">
                        Assigned To: {c.assignee}
                      </span>
                    </div>
                  </div>

                  <>
                    {c.descriptionss && (
                      <div className="mt-3 text-sm leading-relaxed break-words rich-text-content overflow-x-auto w-full">
                        <div dangerouslySetInnerHTML={{ __html: makeLinksSafeAndUserFriendly(c.descriptionss) }} />
                        <CopyButton text={c.descriptionss.replace(/<[^>]*>/g, '').trim()} />
                      </div>
                    )}

                    {/* Recovery & Restart – shown as 2 columns when either field has data */}
                    {(c.recovery_steps || c.restart_steps) && (
                      <div className="mt-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 px-4 py-3">
                        {/* Header */}
                        <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <span>🔄</span> Recovery &amp; Restart
                        </p>
                        {/* Two-column grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                          {/* Problem Recovery */}
                          {c.recovery_steps && (
                            <div className="bg-white rounded-md border border-amber-200 px-3 py-2">
                              <p className="text-[11px] font-bold text-amber-700 uppercase tracking-wide mb-1 flex items-center gap-1">
                                <span>🛠</span> Problem Recovery
                              </p>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                                {c.recovery_steps}
                              </p>
                            </div>
                          )}

                          {/* Restart Procedure */}
                          {c.restart_steps && (
                            <div className="bg-white rounded-md border border-amber-200 px-3 py-2">
                              <p className="text-[11px] font-bold text-amber-700 uppercase tracking-wide mb-1 flex items-center gap-1">
                                <span>🔁</span> Restart Procedure
                              </p>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                                {c.restart_steps}
                              </p>
                            </div>
                          )}

                        </div>
                      </div>
                    )}

                    {c.attachments?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold text-gray-600 flex items-center gap-2 mb-1">
                          <HiPaperClip /> Attachments:
                        </p>
                        <ul className="space-y-1 text-sm text-main">
                          {c.attachments.map((file) => {
                            const fileName = file.filepath.split("/").pop();
                            return (
                              <li
                                key={file.file_id}
                                className="list-disc sm:pl-5 ml-4"
                              >
                                <button
                                  onClick={() => {
                                    const files = c.attachments;
                                    const currentIndex = files.findIndex(
                                      (f) => f.filepath === file.filepath
                                    );
                                    setPreviewImages(files);
                                    setActiveImageIndex(currentIndex);
                                  }}
                                  className="hover:underline text-left w-full"
                                >
                                  {fileName}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </>
                </div>
              ))}
          </div>

          {/* Image Preview Modal */}
          {previewImages.length > 0 && activeImageIndex !== null && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xs p-3 sm:p-6 animate-fade-in">
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[88vh] flex flex-col overflow-hidden border border-gray-100">
                
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50/90 shrink-0">
                  {(() => {
                    const file = previewImages[activeImageIndex];
                    const fileName = file?.filepath ? file.filepath.split("/").pop() : 'Attachment';
                    const fileUrl = `${process.env.REACT_APP_IMAGEURL}${file?.filepath}`;
                    return (
                      <>
                        <div className="flex items-center gap-2 max-w-[55%] truncate">
                          <span className="text-xs font-semibold text-gray-600 bg-gray-200 px-2 py-0.5 rounded">
                            {activeImageIndex + 1} / {previewImages.length}
                          </span>
                          <span className="text-sm font-bold text-gray-800 truncate" title={fileName}>
                            {fileName}
                          </span>
                        </div>

                        {/* Controls (Zoom In, Zoom Out, Reset, Download, Close) */}
                        <div className="flex items-center gap-1.5">
                          {file?.filetype?.startsWith("image/") && (
                            <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-lg border border-gray-200 shadow-xs mr-2">
                              <button
                                type="button"
                                onClick={handleZoomOut}
                                disabled={zoomScale <= 0.5}
                                className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-30 transition cursor-pointer"
                                title="Zoom Out (-)"
                              >
                                <FiZoomOut size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={handleResetZoom}
                                className="text-xs font-bold text-gray-700 hover:text-red-600 px-1.5 py-0.5 rounded transition cursor-pointer"
                                title="Reset Zoom to 100%"
                              >
                                {Math.round(zoomScale * 100)}%
                              </button>
                              <button
                                type="button"
                                onClick={handleZoomIn}
                                disabled={zoomScale >= 3.5}
                                className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-30 transition cursor-pointer"
                                title="Zoom In (+)"
                              >
                                <FiZoomIn size={16} />
                              </button>
                            </div>
                          )}

                          <a
                            href={fileUrl}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg transition"
                            title="Download File"
                          >
                            <FiDownload size={18} />
                          </a>

                          <button
                            type="button"
                            onClick={() => {
                              setPreviewImages([]);
                              setActiveImageIndex(null);
                              setZoomScale(1);
                              setPanPosition({ x: 0, y: 0 });
                            }}
                            className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition ml-1 cursor-pointer"
                            title="Close Preview"
                          >
                            <FiX size={20} />
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Main Preview Container */}
                <div 
                  ref={imageContainerRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  className={`flex-1 w-full bg-slate-900/95 flex items-center justify-center overflow-hidden p-4 relative select-none ${
                    zoomScale > 1 ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'
                  }`}
                  title={zoomScale > 1 ? "Click and drag to pan image | Scroll wheel to zoom" : "Scroll mouse wheel to Zoom In / Zoom Out"}
                >
                  {(() => {
                    const file = previewImages[activeImageIndex];
                    const fileUrl = `${process.env.REACT_APP_IMAGEURL}${file?.filepath}`;

                    if (file?.filetype?.startsWith("image/")) {
                      return (
                        <div className="flex items-center justify-center min-w-full min-h-full">
                          <img
                            src={fileUrl}
                            alt="Preview"
                            draggable={false}
                            style={{
                              transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomScale})`,
                              transition: isPanning ? 'none' : 'transform 0.15s ease-out',
                              transformOrigin: 'center center'
                            }}
                            className="max-h-[72vh] max-w-full object-contain rounded shadow-lg pointer-events-auto select-none"
                          />
                        </div>
                      );
                    }

                    return (
                      <div className="flex flex-col items-center justify-center text-white p-6">
                        <p className="text-gray-300 text-sm mb-3">No direct preview available for this file type.</p>
                        <a
                          href={fileUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition flex items-center gap-2 shadow-md"
                        >
                          <FiDownload size={16} /> Download File
                        </a>
                      </div>
                    );
                  })()}
                </div>

                {/* Footer Navigation */}
                <div className="flex justify-between items-center px-5 py-3 border-t border-gray-200 bg-white shrink-0">
                  <button
                    type="button"
                    onClick={() => setActiveImageIndex((prev) => Math.max(0, prev - 1))}
                    disabled={activeImageIndex === 0}
                    className={`text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-1.5 transition ${
                      activeImageIndex === 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-95 cursor-pointer"
                    }`}
                  >
                    <FiChevronLeft size={16} /> Previous
                  </button>

                  <span className="text-xs font-medium text-gray-500">
                    Image <strong className="text-gray-800">{activeImageIndex + 1}</strong> of {previewImages.length}
                  </span>

                  <button
                    type="button"
                    onClick={() => setActiveImageIndex((prev) => Math.min(previewImages.length - 1, prev + 1))}
                    disabled={activeImageIndex === previewImages.length - 1}
                    className={`text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-1.5 transition ${
                      activeImageIndex === previewImages.length - 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-100 active:scale-95 cursor-pointer"
                    }`}
                  >
                    Next <FiChevronRight size={16} />
                  </button>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
